/**
 * Request coalescing for per-RIC custom functions.
 *
 * A legacy sheet typically has one `=TR(A2,"CF_BID")` or `=RHistory(A2,…)`
 * per row — hundreds of cells that differ only by RIC, all recalculated at
 * once on open. Instead of one /api/evaluate round-trip each, calls that
 * share a group key (same function, fields and parameters) and arrive within
 * a short window are merged into one multi-RIC request; the response is split
 * back per RIC. Identical RICs within a window share one fetch.
 *
 * A failed merged request is first retried as-is when the failure looks
 * transient (5xx / 429 / network — LSEG DataGrid read timeouts surface as
 * 502s under load), then bisected: each half is retried on its own, so a
 * single invalid identifier ends up failing only its own cell in about
 * log2(n) extra calls instead of one call per RIC (which would multiply the
 * load exactly when the backend is struggling). Auth failures and LSEG
 * 422s about the formula itself (an invalid field or display parameter) are
 * neither retried nor bisected, and an outage (backend unreachable) is
 * retried but not bisected: every waiter gets the error.
 */

import { evaluate, ApiError, EvaluateResponse, TableResult, refreshBackoffRemainingMs } from "./api";
import { ricKey, lookupRic, EMPTY_TABLE } from "./refinitiv";
import { isFormulaLevelError } from "./lsegText";

const WINDOW_MS = 50; // how long a group waits for more RICs before it is sent
const MAX_IN_FLIGHT = 4; // concurrent /api/evaluate calls from this runtime
// Of those, at most this many for the "solo" lane (RHistory re-asks and
// default-fields requests, one RIC each), so they never starve the batches.
const MAX_SOLO_IN_FLIGHT = 2;
// Backoff before re-sending a batch that failed transiently. Bisected halves
// get one short retry only: the hub also reports a permanently bad RIC as a
// 502, and full retries at every bisection level would add up.
const RETRY_DELAYS_MS = [1500, 4000];
const HALF_RETRY_DELAYS_MS = [1500];

export interface BatchSpec {
  /** Max RICs per merged request. */
  maxRics: number;
  /** Build the backend formula for a batch of RICs. */
  build(rics: string[]): string;
  /** Split a multi-RIC response into per-RIC tables keyed by ricKey(). */
  split(t: TableResult, rics: string[]): Map<string, TableResult>;
  /**
   * Shape a single-RIC response. That RIC owns every row it gets back
   * (chains expand into constituents whose Instrument column is not the
   * chain RIC), and a lone get_history call isn't RIC-prefixed at all.
   */
  own(t: TableResult): TableResult;
  /**
   * Whether a RIC's slice says nothing about it (no rows, no evidence). In a
   * multi-RIC answer where another RIC did get something, such a slice is
   * marked `emptyInBatch`: lseg.data's multi-RIC get_history fetches each RIC
   * on its own and turns a failed one into an all-NA column with no error
   * (lseg/data/content/_historical_data_provider.py validate_responses,
   * _historical_df_builder.py BadRaws), so the caller may ask it again alone.
   */
  isEmpty?(t: TableResult): boolean;
  /** "solo": the request counts against MAX_SOLO_IN_FLIGHT too. */
  lane?: "solo";
}

interface Waiter {
  resolve(t: TableResult): void;
  reject(e: any): void;
}

interface Entry {
  ric: string;
  waiters: Waiter[];
}

interface Group {
  spec: BatchSpec;
  entries: Map<string, Entry>;
  timer: ReturnType<typeof setTimeout>;
}

const groups = new Map<string, Group>();

// --- Concurrency limit -------------------------------------------------------

let inFlight = 0;
const queue: (() => void)[] = [];
let soloInFlight = 0;
const soloQueue: (() => void)[] = [];

/** The current number of solo-lane requests in flight (tests). */
export const soloRequestsInFlight = (): number => soloInFlight;

async function limited<T>(fn: () => Promise<T>, solo = false): Promise<T> {
  if (solo) {
    if (soloInFlight >= MAX_SOLO_IN_FLIGHT) await new Promise<void>((r) => soloQueue.push(r));
    soloInFlight++;
  }
  try {
    if (inFlight >= MAX_IN_FLIGHT) await new Promise<void>((r) => queue.push(r));
    inFlight++;
    try {
      return await fn();
    } finally {
      inFlight--;
      const next = queue.shift();
      if (next) next();
    }
  } finally {
    if (solo) {
      soloInFlight--;
      const next = soloQueue.shift();
      if (next) next();
    }
  }
}

// --- Batching ----------------------------------------------------------------

function isAuthError(e: any): boolean {
  return e instanceof ApiError && (e.status === 401 || e.status === 403);
}

/**
 * Network failures and 5xx/429 responses are worth re-sending unchanged. A
 * network failure is an ApiError with status 0 (api.ts, fetch rejected) or
 * anything that is not an ApiError at all.
 */
export function isTransient(e: any): boolean {
  return isOutage(e) || e.status >= 500 || e.status === 429;
}

/** A failure to reach the backend at all: it does not depend on the RICs asked. */
function isOutage(e: any): boolean {
  return !(e instanceof ApiError) || e.status === 0;
}

/**
 * Failures every subset of the batch would hit the same way, so bisecting
 * only multiplies calls: an LSEG 422 about the formula itself ("Invalid
 * field(s)", "... is not a valid display parameter.") and, once its retries
 * are spent, an outage. Any other LSEG 422 may hold for some RICs only and
 * is bisected.
 */
function failsEveryRic(e: any, retriesLeft: boolean): boolean {
  return isFormulaLevelError(e) || (isOutage(e) && !retriesLeft);
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

function flush(key: string): void {
  const g = groups.get(key);
  if (!g) return;
  groups.delete(key);
  clearTimeout(g.timer);
  void runBatch(Array.from(g.entries.values()), g.spec);
}

async function runBatch(entries: Entry[], spec: BatchSpec, delays: number[] = RETRY_DELAYS_MS): Promise<void> {
  const rics = entries.map((e) => e.ric);
  try {
    const resp = await limited(() => evaluate(spec.build(rics)), spec.lane === "solo");
    const parts =
      rics.length === 1 ? new Map([[ricKey(rics[0]), spec.own(resp.result)]]) : spec.split(resp.result, rics);
    const slices = entries.map((e) => lookupRic(parts, e.ric) || EMPTY_TABLE);
    // Every RIC empty is an answer (a window without rows), not a swallowed failure.
    const empty = rics.length > 1 && spec.isEmpty ? slices.map((t) => spec.isEmpty!(t)) : [];
    const someFull = empty.some((x) => !x);
    entries.forEach((e, i) => {
      const t = someFull && empty[i] ? { ...slices[i], emptyInBatch: true } : slices[i];
      e.waiters.forEach((w) => w.resolve(t));
    });
  } catch (err) {
    if (isAuthError(err) || failsEveryRic(err, delays.length > 0)) {
      entries.forEach((e) => e.waiters.forEach((w) => w.reject(err)));
    } else if (isTransient(err) && delays.length) {
      // Never retry inside a session-refresh back-off: that try would only meet
      // the cached refresh error and spend a retry for nothing.
      await sleep(Math.max(delays[0], refreshBackoffRemainingMs()));
      await runBatch(entries, spec, delays.slice(1));
    } else if (entries.length > 1) {
      const mid = Math.ceil(entries.length / 2);
      await Promise.all([
        runBatch(entries.slice(0, mid), spec, HALF_RETRY_DELAYS_MS),
        runBatch(entries.slice(mid), spec, HALF_RETRY_DELAYS_MS),
      ]);
    } else {
      entries.forEach((e) => e.waiters.forEach((w) => w.reject(err)));
    }
  }
}

/** The longest a 429's Retry-After is waited for here. */
const MAX_RETRY_AFTER_MS = 10000;

/**
 * One /api/evaluate call that is not merged by RIC (RDP.Search: one request
 * per cell), under the same concurrency limit as the batches and retried as
 * runBatch retries a merged request: transient failures (network, 5xx, 429)
 * are re-sent after RETRY_DELAYS_MS (a 429's Retry-After and a session-refresh
 * back-off stretch the wait). Auth failures and `isAnswer` failures (the
 * service refused the request itself) are thrown at once.
 */
export async function evaluateLimited(
  formula: string,
  isAnswer: (e: unknown) => boolean = () => false
): Promise<EvaluateResponse> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await limited(() => evaluate(formula));
    } catch (err: any) {
      if (isAuthError(err) || isAnswer(err) || !isTransient(err) || attempt >= RETRY_DELAYS_MS.length) throw err;
      const retryAfter = Math.min(Number(err?.retryAfterMs) || 0, MAX_RETRY_AFTER_MS);
      await sleep(Math.max(RETRY_DELAYS_MS[attempt], retryAfter, refreshBackoffRemainingMs()));
    }
  }
}

/** Queue one RIC under a group key; resolves with that RIC's slice of the merged response. */
export function fetchRic(groupKey: string, ric: string, spec: BatchSpec): Promise<TableResult> {
  // Chain RICs (0#.FCHI) expand server-side, so they can't share a request.
  const key = ric.startsWith("0#") ? `${groupKey}|${ricKey(ric)}` : groupKey;
  return new Promise<TableResult>((resolve, reject) => {
    let g = groups.get(key);
    if (!g) {
      g = { spec, entries: new Map(), timer: setTimeout(() => flush(key), WINDOW_MS) };
      groups.set(key, g);
    }
    const k = ricKey(ric);
    if (!g.entries.has(k)) g.entries.set(k, { ric, waiters: [] });
    g.entries.get(k)!.waiters.push({ resolve, reject });
    if (g.entries.size >= spec.maxRics) flush(key);
  });
}

/** What fetchRicsSettled returns. All maps are keyed by ricKey(). */
export interface SettledRics {
  /** Each RIC's slice; null when its fetch failed (EMPTY_TABLE when it was simply absent). */
  results: Map<string, TableResult | null>;
  /** The error each failed RIC was rejected with, so callers can map it to a cell text. */
  errors: Map<string, any>;
  /** The first per-RIC error, or null. */
  error: any;
}

/**
 * Fetch every distinct non-blank RIC in `rics` through the batcher and settle
 * each one: failed RICs map to null in `results` and keep their error in
 * `errors`. Auth errors still throw — nothing else can succeed either.
 */
export async function fetchRicsSettled(groupKey: string, rics: string[], spec: BatchSpec): Promise<SettledRics> {
  const unique = Array.from(new Map(rics.filter(Boolean).map((r) => [ricKey(r), r] as [string, string])).values());
  const settled = await Promise.all(
    unique.map((r) =>
      fetchRic(groupKey, r, spec).then(
        (t) => ({ r, t: t as TableResult | null, e: null as any }),
        (e) => ({ r, t: null as TableResult | null, e })
      )
    )
  );
  const auth = settled.find((s) => s.e && isAuthError(s.e));
  if (auth) throw auth.e;
  const failed = settled.filter((s) => s.e);
  return {
    results: new Map(settled.map((s) => [ricKey(s.r), s.t] as [string, TableResult | null])),
    errors: new Map(failed.map((s) => [ricKey(s.r), s.e] as [string, any])),
    error: failed.length ? failed[0].e : null,
  };
}

/**
 * Like fetchRicsSettled, but rethrows when every RIC failed so the cell
 * shows the reason; per-RIC failures map to null (rendered as a blank row).
 */
export async function fetchRics(
  groupKey: string,
  rics: string[],
  spec: BatchSpec
): Promise<Map<string, TableResult | null>> {
  const { results, error } = await fetchRicsSettled(groupKey, rics, spec);
  if (error && results.size && Array.from(results.values()).every((t) => t === null)) throw error;
  return results;
}
